import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { RegistrationFormData, RegistrationFormSteps } from "./steps/StepModel";
import { container } from "setup/dependency/inversify.config";
import { IClientPortalUsersService } from "services/ClientPortalUsersService";
import { SERVICE_TYPES } from "setup/dependency/ServiceTypes";
import { RootState } from "../../../../../setup/redux/RootReducer";
import { NewClientPortalUserModel } from "../../../../../services/api/cplauthapi/CPLAuthAPI";
import { actions } from "../../redux/AuthRedux";

enum ValidationMessages {
  InvalidEmailAddress = "The email address provided is invalid",
  InvalidVerificationCode = "Invalid verification code",
  InvalidRegistrationDetails = "The account details provided are invalid",
}

type RegistrationPageState = {
  Loading: boolean;
  CurrentStep: RegistrationFormSteps;
  Status: string;
  AllowResendVerificationCode: boolean;
};

const initialState: RegistrationPageState = {
  Loading: false,
  CurrentStep: RegistrationFormSteps.EmailAddress,
  Status: "",
  AllowResendVerificationCode: true,
};

export const registrationPageSlice = createSlice({
  name: "registration",
  initialState,
  reducers: {
    setLoading: (state, action) => {
      state.Loading = action.payload;
    },
    setStatus: (state, action) => {
      state.Status = action.payload;
    },
    enableVerificationCodeResend: (state) => {
      state.AllowResendVerificationCode = true;
    },
    incrementCurrentStep: (state) => {
      state.CurrentStep += 1;
      state.Status = "";
    },
    decrementCurrentStep: (state) => {
      if (state.CurrentStep > 0) {
        state.CurrentStep -= 1;
        state.Status = "";
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(sendEmailVerificationCode.pending, (state) => {
        state.Loading = true;
        state.Status = "";
      })
      .addCase(sendEmailVerificationCode.fulfilled, (state) => {
        state.Loading = false;
        state.AllowResendVerificationCode = false;
      })
      .addCase(sendEmailVerificationCode.rejected, (state, action) => {
        state.Loading = false;
        state.Status = action.error.message ?? ValidationMessages.InvalidEmailAddress;
        state.AllowResendVerificationCode = true;
      })
      .addCase(verifyEmail.pending, (state) => {
        state.Loading = true;
        state.Status = "";
      })
      .addCase(verifyEmail.fulfilled, (state) => {
        state.Loading = false;
      })
      .addCase(verifyEmail.rejected, (state) => {
        state.Loading = false;
        state.Status = ValidationMessages.InvalidVerificationCode;
      })
      .addCase(registerNewUser.pending, (state) => {
        state.Loading = true;
      })
      .addCase(registerNewUser.fulfilled, (state) => {
        state.Loading = false;
      })
      .addCase(registerNewUser.rejected, (state) => {
        state.Loading = false;
        state.Status = ValidationMessages.InvalidRegistrationDetails;
      });
  },
});

export const sendEmailVerificationCode = createAsyncThunk<
  void,
  { emailAddress: string; incrementStep?: boolean },
  { state: RootState }
>("RegistrationPage/SendEmailVerificationCode", async ({ emailAddress, incrementStep = true }, thunkAPI) => {
  const clientPortalUsersService = container.get<IClientPortalUsersService>(SERVICE_TYPES.ClientPortalUsersService);
  const state = thunkAPI.getState();
  await clientPortalUsersService.sendEmailVerificationCode(emailAddress, state.companyProvider.companyId ?? "");
  if (incrementStep) thunkAPI.dispatch(incrementCurrentStep());
});

export const verifyEmail = createAsyncThunk<
  void,
  { emailAddress: string; verificationCode: string },
  { state: RootState }
>("RegistrationPage/VerifyEmail", async ({ emailAddress, verificationCode }, thunkAPI) => {
  const clientPortalUsersService = container.get<IClientPortalUsersService>(SERVICE_TYPES.ClientPortalUsersService);
  const state = thunkAPI.getState();
  await clientPortalUsersService.verifyEmail(emailAddress, verificationCode, state.companyProvider.companyId ?? "");
  thunkAPI.dispatch(incrementCurrentStep());
});

export const registerNewUser = createAsyncThunk<
  void,
  { newClientPortalUser: RegistrationFormData },
  { state: RootState }
>("RegistationPage/RegisterNewUser", async ({ newClientPortalUser }, thunkAPI) => {
  const clientPortalUsersService = container.get<IClientPortalUsersService>(SERVICE_TYPES.ClientPortalUsersService);
  const state = thunkAPI.getState();
  await clientPortalUsersService.createNewClientPortalUser(
    new NewClientPortalUserModel({
      emailAddress: newClientPortalUser.emailAddress,
      companyID: state.companyProvider.companyId ?? "",
      emailVerificationCode: newClientPortalUser.verificationCode,
      password: newClientPortalUser.password,
      firstName: newClientPortalUser.firstName,
      lastName: newClientPortalUser.lastName,
    })
  );
  thunkAPI.dispatch(
    actions.login({
      companyID: state.companyProvider.companyId ?? "",
      emailAddress: newClientPortalUser.emailAddress!,
      password: newClientPortalUser.password!,
    })
  );
});

export const { setLoading, incrementCurrentStep, decrementCurrentStep, setStatus, enableVerificationCodeResend } =
  registrationPageSlice.actions;
export const selectStatus = (state: RootState) => state.registration.Status;
export const selectLoading = (state: RootState) => state.registration.Loading;
export const selectCurrentStep = (state: RootState) => state.registration.CurrentStep;
export const selectAllowResendVerificationCode = (state: RootState) => state.registration.AllowResendVerificationCode;

export default registrationPageSlice.reducer;
